import { cloneDeep, cloneDeepWith } from 'lodash';
import uuidHelper, { findWithUUID, removeItemByUUID } from '@/utils/uuidHelper';
import {
  assignValue, hasKey, replaceEmptyString, compareByName,
} from '@/utils/resultsHelper';
import { pruneFieldErrors } from '@/utils/errors';
import { isSplitUrl, joinUrl, splitUrl } from '@/constants/urls';
import fields from '@/constants/fields';
import { openEntity, openEntityDialog } from '@/constants/states';
import { readStates, saveStates } from '@/constants/lifecycle';
import findDataset from '../../state/shared';
import newDictionaryField, { assignLookupToField } from '../../fields/state';
import { datasetPerms } from '../../sharing/state/state';
import newLookupItem, { activeLookup, assignLookupEditProps } from '../../lookups/state';
import { dictionaryReportTypes } from '../../reports/constants';
import { validationWarningDictionaryLookups } from '../../lookups/helper';

const newDictionary = (properties) => {
  const dictionary = {
    code: properties.code,
    description: properties.description,
    fields: [
      newDictionaryField(),
    ],
    lookups: {},
    primary: properties.primary,
    name: properties.name,
    uuid: uuidHelper.generate(),
  };
  if (properties.source_name) {
    dictionary.source_name = properties.source_name;
  }
  return dictionary;
};

const primaryDictionaryDefault = { fields: [{ name: '' }], name: '', uuid: null };

export const firstDictionary = (dictionaries, defaultIfNoExists = primaryDictionaryDefault) => {
  const first = dictionaries.reduce((a, b) => (compareByName(a, b) <= 0 ? a : b), dictionaries[0]);
  return first || defaultIfNoExists;
};

export const primaryDictionary = ({ dictionaries }, defaultIfNoExists = primaryDictionaryDefault) => {
  const dictionary = dictionaries.find(({ primary }) => primary);
  return dictionary || firstDictionary(dictionaries, defaultIfNoExists);
};

export const applyPrimaryDictionary = ({ dictionaries }, { uuid }) => (
  dictionaries.forEach((dictionary) => assignValue(dictionary, 'primary', dictionary.uuid === uuid))
);

export const splitUrlsInDictionaries = ({ dictionaries }) => {
  dictionaries.forEach((dictionary) => {
    dictionary.fields.forEach((field) => assignValue(field, 'uri', splitUrl(field.uri)));
    Object.keys(dictionary.lookups).forEach((key) => assignLookupEditProps(dictionary.lookups[key]));
  });
};

export const pruneActiveDictionaryEditErrors = (errors, { properties }) => {
  let filteredErrors = errors;

  if (hasKey(properties, 'dictionaryCode')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.CODE);
  }

  if (hasKey(properties, 'dictionaryName')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.NAME);
  }

  if (hasKey(properties, 'dictionarySourceName')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.SOURCE_NAME);
  }

  if (hasKey(properties, 'dictionaryDescription')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.DESCRIPTION);
  }

  return filteredErrors;
};

export const pruneActiveDictionaryErrors = (errors, { properties }) => {
  let filteredErrors = errors;

  if (hasKey(properties, 'dictionaryName')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.NAME);
  }

  return filteredErrors;
};

export const activeDictionaries = () => ({
  ...openEntity,
  activeDictionaryUUID: '',
  activeFieldUUID: '',
  activeLookupName: '',
  dictionaries: [],
  fieldsOpen: false,
  isDirty: false,
  isEditing: false,
  isNew: false,
  lookupBeingEdited: {
    isEditing: false,
    isNew: false,
    name: '',
    ...saveStates,
  },
  lookupsOpen: false,
  permissions: datasetPerms(),
  ...saveStates,
  validationWarnings: [],
});

export const activeDictionary = () => ({
  ...openEntityDialog,
  dictionary: null,
  dictionaryFromDatafile: {
    deleteSource: false,
    filter: '',
    items: [],
    ...readStates,
  },
  isNew: false,
  ...saveStates,
});

export const activeDictionaryEdit = () => ({
  ...openEntityDialog,
  dictionaryCode: null,
  dictionaryDescription: '',
  dictionaryName: '',
  primary: false,
  dictionarySourceName: '',
  dictionaryUUID: null,
  isNew: false,
  isPersisted: false,
  originalName: '',
  ...saveStates,
});

export const activeDictionaryValidationResult = () => ({
  ...openEntityDialog,
  dictionaryCode: null,
  dictionaryName: null,
  reportType: dictionaryReportTypes.VALIDATION,
  validationReportName: null,
});

export const assignEditPropsToActiveLookups = (lookups, withValidation) => {
  const assignableLookups = lookups;
  const errors = lookups.saveErrors.filter(
    (e) => (e.context.dictionaryUUID !== lookups.activeDictionaryUUID)
          || (e.context.dictionaryUUID === lookups.activeDictionaryUUID
              && e.context.lookupName !== lookups.activeLookupName),
  );
  let { validationWarnings } = lookups;

  if (withValidation) {
    validationWarnings = lookups.validationWarnings.filter(
      (e) => (e.context.dictionaryUUID !== lookups.activeDictionaryUUID)
            || (e.context.dictionaryUUID === lookups.activeDictionaryUUID
                && e.context.lookupName !== lookups.activeLookupName),
    );
    const dictionary = findWithUUID(lookups.dictionaries, lookups.activeDictionaryUUID);
    validationWarningDictionaryLookups(dictionary.lookups, dictionary.uuid, validationWarnings);
  }

  if (!assignableLookups.isEditing) {
    splitUrlsInDictionaries(assignableLookups);
    assignableLookups.isEditing = true;
  }
  assignableLookups.saveErrors = errors;
  assignableLookups.validationWarnings = validationWarnings;
  assignableLookups.isDirty = true;
};

export const updateLookupType = (lookups, { dataType, pmnFunctions }) => {
  const assignableLookups = lookups;
  activeLookup(assignableLookups).type = dataType;
  assignLookupToField(assignableLookups, lookups.activeLookupName, dataType, pmnFunctions);
  assignEditPropsToActiveLookups(lookups);
};

const editLookupName = (lookups) => {
  const assignableLookups = lookups;
  const oldName = assignableLookups.activeLookupName;
  const newName = assignableLookups.lookupBeingEdited.name;

  if (oldName !== newName) {
    const dictionary = assignLookupToField(assignableLookups, newName);

    dictionary.lookups[newName] = dictionary.lookups[oldName];
    delete dictionary.lookups[oldName];
    assignableLookups.activeLookupName = newName;
  }
};

export const updateLookupName = (lookups) => {
  const assignableLookups = lookups;
  const dictionary = findWithUUID(assignableLookups.dictionaries, lookups.activeDictionaryUUID);
  const isNew = assignableLookups.lookupBeingEdited.isNew
    || !dictionary.lookups
    || Object.keys(dictionary.lookups).length === 0;

  assignEditPropsToActiveLookups(lookups);
  if (isNew) {
    if (!dictionary.lookups) {
      dictionary.lookups = {};
    }
    dictionary.lookups[assignableLookups.lookupBeingEdited.name] = {
      options: [newLookupItem(), newLookupItem()],
      type: 'text',
    };
    assignableLookups.activeLookupName = assignableLookups.lookupBeingEdited.name;
  } else {
    editLookupName(lookups);
  }
};

export const removeLookupItem = (lookups, { lookupItemUUID }) => {
  const dictionary = findWithUUID(lookups.dictionaries, lookups.activeDictionaryUUID);
  const { activeLookupName } = lookups;
  const { options } = dictionary.lookups[activeLookupName];

  dictionary.lookups[activeLookupName].options = removeItemByUUID(options, lookupItemUUID);
  assignEditPropsToActiveLookups(lookups, true);
};

export const cloneDictionary = (dictionary) => {
  const copy = cloneDeep(dictionary);
  copy.code = null;
  delete copy.id;
  delete copy.created_at;
  delete copy.updated_at;
  delete copy.source_name;
  delete copy.status;

  return copy;
};

export const cloneDictionaries = ({ dictionaries }) => dictionaries.map(cloneDictionary);

const valueTransform = (value) => (isSplitUrl(value) ? joinUrl(value) : replaceEmptyString(value));

export const transformDictionaries = (dictionaries) => cloneDeepWith(dictionaries, valueTransform);

export const findDatasetDictionary = (
  { datasets },
  { category, code, dictionaryUUID },
  dictionaryIfNoExists = null,
) => {
  const item = findDataset({ datasets }, { category, code });
  const dictionary = dictionaryUUID ? findWithUUID(item.dictionaries || [], dictionaryUUID) : null;
  return { item, dictionary: dictionary || dictionaryIfNoExists };
};

export const findActiveDatasetDictionary = ({ datasets }, { category, code }) => {
  const item = findDataset({ datasets }, { category, code });
  return { item, dictionary: findWithUUID(item.dictionaries || [], item.activeDictionaryUUID) };
};

export const findDatasetDictionaryField = ({ datasets }, {
  category,
  code,
  dictionaryUUID,
  fieldUUID,
}) => {
  const { item, dictionary } = findDatasetDictionary({ datasets }, { category, code, dictionaryUUID });
  return { item, dictionary, field: dictionary ? findWithUUID(dictionary.fields, fieldUUID) : null };
};

export default newDictionary;
